import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Grid,
  Button,
  Chip,
  Avatar,
  AvatarGroup,
  IconButton,
  Stack,
  TextField,
  InputAdornment,
  CircularProgress,
  Divider,
  Alert,
  Tooltip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Tabs,
  Tab,
  Card,
} from '@mui/material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { CiSearch } from 'react-icons/ci';
import { IoAddOutline, IoGridOutline, IoListOutline, IoArrowForward, IoSchoolOutline } from 'react-icons/io5';
import { FaGraduationCap, FaChalkboardTeacher, FaUserGraduate } from 'react-icons/fa';
import { MdOutlineAccountBalanceWallet } from 'react-icons/md';
import { Icon } from '@iconify/react';
import moment from 'moment';

import Page from 'src/components/Page';
import palette from 'src/theme/palette';
import { alpha } from '@mui/material';
import { useAuth } from 'src/context/auth/AuthContext';
import { classService } from 'src/services/class.service';
import { cohortService } from 'src/services/cohort.service';
import { CohortWithDetails, CreateCohortDTO } from 'src/types/cohort.types';
import { CreateCohortModal } from 'src/components/cohorts/CreateCohortModal';
import { CreateClassModal } from 'src/components/cohorts/CreateClassModal';
import { EditClassModal } from 'src/components/cohorts/EditClassModal';
import { DeleteClassModal } from 'src/components/cohorts/DeleteClassModal';
import { EditCohortModal } from 'src/components/cohorts/EditCohortModal';
import { AssignTeachersModal } from 'src/components/cohorts/AssignTeachersModal';
import { DeleteCohortModal } from 'src/components/cohorts/DeleteCohortModal';
import { CohortCard } from 'src/components/cohorts/CohortCard';
import CustomPagination from 'src/components/CustomPagination';
import SearchNotFound from 'src/components/SearchNotFound';

export default function CohortsHub() {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { user, hasPermission, getBaseRoute } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const classIdParam = searchParams.get('classId');

  const [currentTab, setCurrentTab] = useState<number>(0);
  const [searchText, setSearchText] = useState('');
  const [classSearchText, setClassSearchText] = useState('');
  const [selectedClassId, setSelectedClassId] = useState<string>(classIdParam || 'ALL');
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('grid');

  // Fine-grained RBAC permissions
  const canViewAllCohorts = hasPermission('cohorts.view_all');
  const canCreateCohort = hasPermission('cohorts.create');
  const canEditCohort = hasPermission('cohorts.edit');
  const canDeleteCohort = hasPermission('cohorts.delete');
  const canManageTeachers = hasPermission('cohorts.manage_teachers');
  const canManageClasses = hasPermission('classes.create');

  // Dynamic route based on role base route
  const baseRoute = getBaseRoute('/cohorts');

  useEffect(() => {
    if (classIdParam) {
      setSelectedClassId(classIdParam);
    } else if (classIdParam === null && selectedClassId !== 'ALL') {
      setSelectedClassId('ALL');
    }
  }, [classIdParam]);

  // Modals state
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isCreateClassOpen, setIsCreateClassOpen] = useState(false);
  const [editingCohort, setEditingCohort] = useState<CohortWithDetails | null>(null);
  const [assignTeachersCohort, setAssignTeachersCohort] = useState<CohortWithDetails | null>(null);
  const [cohortToDelete, setCohortToDelete] = useState<CohortWithDetails | null>(null);
  const [editingClass, setEditingClass] = useState<any | null>(null);
  const [classToDelete, setClassToDelete] = useState<any | null>(null);

  // 1. Fetch Classes (Parent Grades)
  const { data: classResponse, isLoading: isLoadingClasses } = useQuery({
    queryKey: ['classes-list'],
    queryFn: () => classService.getClasses({}),
  });

  const classes = classResponse?.data || [];

  // 2. Fetch Cohorts (Filtered if teacher doesn't have permission to view all)
  const {
    data: cohorts = [],
    isLoading: isLoadingCohorts,
    error: cohortsError,
  } = useQuery({
    queryKey: [
      'cohorts-list',
      {
        classId: selectedClassId,
        search: searchText,
        teacherUserId: !canViewAllCohorts ? (user?.id || user?.email) : undefined,
      },
    ],
    queryFn: () =>
      cohortService.getCohorts({
        classId: selectedClassId === 'ALL' ? undefined : selectedClassId,
        search: searchText,
        teacherUserId: !canViewAllCohorts ? (user?.id || user?.email) : undefined,
      }),
  });

  // Class & Cohort Mutations
  const createClassMutation = useMutation({
    mutationFn: (title: string) => classService.createClass({ title }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['classes-list'] });
      toast.success('Grade Level created successfully.');
      setIsCreateClassOpen(false);
    },
    onError: (err: any) => {
      toast.error(err?.message || 'Failed to create Grade Level.');
    },
  });

  const updateClassMutation = useMutation({
    mutationFn: ({ id, title }: { id: string; title: string }) => classService.updateClass(id, { name: title }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['classes-list'] });
      queryClient.invalidateQueries({ queryKey: ['cohorts-list'] });
      toast.success('Grade Level updated successfully.');
      setEditingClass(null);
    },
    onError: (err: any) => {
      toast.error(err?.message || 'Failed to update Grade Level.');
    },
  });

  const deleteClassMutation = useMutation({
    mutationFn: (id: string) => classService.deleteClass(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['classes-list'] });
      queryClient.invalidateQueries({ queryKey: ['cohorts-list'] });
      toast.success('Grade Level deleted successfully.');
      setClassToDelete(null);
    },
    onError: (err: any) => {
      toast.error(err?.message || 'Failed to delete Grade Level.');
    },
  });

  const createCohortMutation = useMutation({
    mutationFn: (dto: CreateCohortDTO) => cohortService.createCohort(dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cohorts-list'] });
      toast.success('Cohort section created successfully.');
      setIsCreateOpen(false);
    },
    onError: (err: any) => {
      toast.error(err?.message || 'Failed to create cohort section.');
    },
  });

  const updateCohortMutation = useMutation({
    mutationFn: ({ id, dto }: { id: string; dto: Partial<CreateCohortDTO> }) => cohortService.updateCohort(id, dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cohorts-list'] });
      toast.success('Cohort updated successfully.');
      setEditingCohort(null);
    },
    onError: (err: any) => {
      toast.error(err?.message || 'Failed to update cohort.');
    },
  });

  const assignTeachersMutation = useMutation({
    mutationFn: ({ cohortId, teacherIds }: { cohortId: string; teacherIds: string[] }) =>
      cohortService.assignTeachersToCohort(cohortId, teacherIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cohorts-list'] });
      toast.success('Teachers assigned successfully.');
      setAssignTeachersCohort(null);
    },
    onError: (err: any) => {
      toast.error(err?.message || 'Failed to assign teachers.');
    },
  });

  const deleteCohortMutation = useMutation({
    mutationFn: (id: string) => cohortService.deleteCohort(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cohorts-list'] });
      toast.success('Cohort section deleted successfully.');
      setCohortToDelete(null);
    },
    onError: (err: any) => {
      toast.error(err?.message || 'Failed to delete cohort section.');
    },
  });

  // Calculate aggregates
  const totalScholars = useMemo(() => {
    return cohorts.reduce((sum, c) => sum + (c.studentCount || 0), 0);
  }, [cohorts]);

  const totalVaultBalance = useMemo(() => {
    return cohorts.reduce((sum, c) => sum + (c.totalBalance || 0), 0);
  }, [cohorts]);

  const visibleClasses = useMemo(() => {
    if (canViewAllCohorts) return classes;
    const assignedClassIds = new Set(cohorts.map((c) => c.class_id));
    return classes.filter((cls) => assignedClassIds.has(cls.id));
  }, [classes, cohorts, canViewAllCohorts]);

  // Filtered Classes for Tab 1
  const filteredClasses = useMemo(() => {
    if (!classSearchText.trim()) return classes;
    const q = classSearchText.toLowerCase();
    return classes.filter(
      (c) =>
        c.name?.toLowerCase().includes(q) ||
        c.title?.toLowerCase().includes(q) ||
        c.id?.toLowerCase().includes(q)
    );
  }, [classes, classSearchText]);

  // Map of cohorts by class ID for quick lookup
  const cohortsByClassId = useMemo(() => {
    const map = new Map<string, CohortWithDetails[]>();
    cohorts.forEach((cohort) => {
      const arr = map.get(cohort.class_id) || [];
      arr.push(cohort);
      map.set(cohort.class_id, arr);
    });
    return map;
  }, [cohorts]);

  const handleClassFilterClick = (classId: string) => {
    setSelectedClassId(classId);
    if (classId === 'ALL') {
      searchParams.delete('classId');
    } else {
      searchParams.set('classId', classId);
    }
    setSearchParams(searchParams);
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  return (
    <Page title={!canViewAllCohorts ? 'My Cohorts | Gold Minds Portal' : 'Cohorts & Classes | Gold Minds Portal'}>
      <Box sx={{ width: '100%', px: { xs: 1, sm: 1.5, md: 2 }, pt: 0.5, pb: 4 }}>
        {/* Header Ribbon */}
        <Box sx={{ mb: 2, mt: 0.25 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={1.5}>
            <Box>
              <Stack direction="row" alignItems="center" spacing={1.25}>
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: 2,
                    bgcolor: 'rgba(37, 99, 235, 0.12)',
                    color: '#2563EB',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Icon icon="solar:users-group-two-rounded-bold-duotone" width={22} />
                </Box>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: 'text.primary', letterSpacing: -0.3, fontSize: '1.35rem' }}>
                    {!canViewAllCohorts ? 'My Cohort Sections' : 'Cohorts & Academic Hierarchy'}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                    {!canViewAllCohorts
                      ? 'View and manage your assigned student sections, explore scholar rosters, and supervise classroom economies.'
                      : 'Organize grade levels into sections, manage scholar rosters, and monitor collective classroom economies.'}
                  </Typography>
                </Box>
              </Stack>
            </Box>

            <Stack direction="row" spacing={1} alignItems="center">
              {canManageClasses && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<Icon icon="solar:add-circle-bold" width={16} />}
                  onClick={() => setIsCreateClassOpen(true)}
                  sx={{
                    height: 36,
                    borderRadius: 1.5,
                    fontWeight: 600,
                    fontSize: '0.8125rem',
                    textTransform: 'none',
                    borderColor: 'divider',
                    color: 'text.primary',
                    '&:hover': { bgcolor: 'grey.100', borderColor: 'text.secondary' },
                  }}
                >
                  Add Grade Level
                </Button>
              )}

              {canCreateCohort && (
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<Icon icon="solar:add-circle-bold" width={18} />}
                  onClick={() => setIsCreateOpen(true)}
                  sx={{
                    height: 36,
                    px: 2,
                    fontWeight: 600,
                    fontSize: '0.8125rem',
                    borderRadius: 1.5,
                    textTransform: 'none',
                    boxShadow: 'none',
                    '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.15)' },
                  }}
                >
                  New Cohort
                </Button>
              )}
            </Stack>
          </Stack>
        </Box>

        {/* 4 Metric Cards */}
        <Grid container spacing={1.5} sx={{ mb: 2 }}>
          <Grid item xs={6} sm={3}>
            <Card
              sx={{
                p: 1.75,
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                boxShadow: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Box>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: '0.6875rem' }}>
                  {!canViewAllCohorts ? 'Assigned Grades' : 'Grade Tiers'}
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, color: 'text.primary', my: 0.25, fontSize: '1.25rem' }}>
                  {visibleClasses.length}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.6875rem' }}>
                  Academic Grade Containers
                </Typography>
              </Box>
              <Box sx={{ width: 38, height: 38, borderRadius: 1.75, bgcolor: 'rgba(37, 99, 235, 0.12)', color: '#2563EB', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <FaGraduationCap size={18} />
              </Box>
            </Card>
          </Grid>

          <Grid item xs={6} sm={3}>
            <Card
              sx={{
                p: 1.75,
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                boxShadow: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Box>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: '0.6875rem' }}>
                  {!canViewAllCohorts ? 'My Cohorts' : 'Active Sections'}
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, color: 'text.primary', my: 0.25, fontSize: '1.25rem' }}>
                  {cohorts.length}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.6875rem' }}>
                  Classroom Cohorts
                </Typography>
              </Box>
              <Box sx={{ width: 38, height: 38, borderRadius: 1.75, bgcolor: 'rgba(124, 58, 237, 0.12)', color: '#7C3AED', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <IoSchoolOutline size={18} />
              </Box>
            </Card>
          </Grid>

          <Grid item xs={6} sm={3}>
            <Card
              sx={{
                p: 1.75,
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                boxShadow: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Box>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: '0.6875rem' }}>
                  {!canViewAllCohorts ? 'Supervised Scholars' : 'Enrolled Scholars'}
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#059669', my: 0.25, fontSize: '1.25rem' }}>
                  {totalScholars}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.6875rem' }}>
                  Total active students
                </Typography>
              </Box>
              <Box sx={{ width: 38, height: 38, borderRadius: 1.75, bgcolor: 'rgba(16, 185, 129, 0.12)', color: '#059669', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <FaUserGraduate size={16} />
              </Box>
            </Card>
          </Grid>

          <Grid item xs={6} sm={3}>
            <Card
              sx={{
                p: 1.75,
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                boxShadow: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Box>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: '0.6875rem' }}>
                  {!canViewAllCohorts ? 'Supervised Vault' : 'Combined Vault'}
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, color: 'text.primary', my: 0.25, fontSize: '1.25rem' }}>
                  ${totalVaultBalance.toLocaleString()}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.6875rem' }}>
                  Total circulating currency
                </Typography>
              </Box>
              <Box sx={{ width: 38, height: 38, borderRadius: 1.75, bgcolor: 'rgba(245, 158, 11, 0.12)', color: '#D97706', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <MdOutlineAccountBalanceWallet size={18} />
              </Box>
            </Card>
          </Grid>
        </Grid>

        {/* Navigation Tabs */}
        <Box sx={{ mb: 2 }}>
          <Tabs
            value={currentTab}
            onChange={handleTabChange}
            sx={{
              '& .MuiTabs-indicator': {
                height: 3,
                borderRadius: '3px 3px 0 0',
              },
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.875rem',
                minHeight: 44,
                py: 1,
                px: 2,
                color: 'text.secondary',
                '&.Mui-selected': {
                  color: 'primary.main',
                  fontWeight: 700,
                },
              },
            }}
          >
            <Tab
              icon={<Icon icon="solar:users-group-two-rounded-bold-duotone" width={18} />}
              iconPosition="start"
              label={`Cohort Sections Directory (${cohorts.length})`}
            />
            {canManageClasses && (
              <Tab
                icon={<Icon icon="solar:diploma-verified-bold-duotone" width={18} />}
                iconPosition="start"
                label={`Academic Classes & Grade Tiers (${classes.length})`}
              />
            )}
          </Tabs>
        </Box>

        {/* TAB 0: Cohort Sections Directory */}
        {currentTab === 0 && (
          <Box>
            {/* Filter & View Mode Controls Bar */}
            <Card
              sx={{
                p: 1.5,
                mb: 2,
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                boxShadow: 'none',
                bgcolor: 'background.neutral',
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                alignItems: { xs: 'stretch', md: 'center' },
                justifyContent: 'space-between',
                gap: 1.5,
              }}
            >
              {/* Search Field */}
              <TextField
                size="small"
                placeholder="Search cohorts by name, teacher, or grade..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CiSearch size={18} color="#94A3B8" />
                    </InputAdornment>
                  ),
                  sx: {
                    height: 36,
                    bgcolor: 'background.paper',
                    borderRadius: 1.5,
                    fontSize: '0.8125rem',
                    maxWidth: { xs: '100%', md: 320 },
                  },
                }}
              />

              {/* Grade Filter Chips & View Mode Toggle */}
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Stack direction="row" spacing={0.75} sx={{ overflowX: 'auto', py: 0.25 }}>
                  <Chip
                    label="All Grades"
                    size="small"
                    onClick={() => handleClassFilterClick('ALL')}
                    sx={{
                      height: 32,
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      cursor: 'pointer',
                      bgcolor: selectedClassId === 'ALL' ? 'primary.main' : 'background.paper',
                      color: selectedClassId === 'ALL' ? '#FFFFFF' : 'text.primary',
                      border: '1px solid',
                      borderColor: selectedClassId === 'ALL' ? 'primary.main' : 'divider',
                      '&:hover': { bgcolor: selectedClassId === 'ALL' ? 'primary.dark' : 'grey.100' },
                    }}
                  />
                  {visibleClasses.map((cls) => (
                    <Chip
                      key={cls.id}
                      label={cls.name || cls.title}
                      size="small"
                      onClick={() => handleClassFilterClick(cls.id)}
                      sx={{
                        height: 32,
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        cursor: 'pointer',
                        bgcolor: selectedClassId === cls.id ? 'primary.main' : 'background.paper',
                        color: selectedClassId === cls.id ? '#FFFFFF' : 'text.primary',
                        border: '1px solid',
                        borderColor: selectedClassId === cls.id ? 'primary.main' : 'divider',
                        '&:hover': { bgcolor: selectedClassId === cls.id ? 'primary.dark' : 'grey.100' },
                      }}
                    />
                  ))}
                </Stack>

                <ToggleButtonGroup
                  value={viewMode}
                  exclusive
                  onChange={(_, val) => val && setViewMode(val)}
                  size="small"
                  sx={{
                    bgcolor: 'background.paper',
                    borderRadius: 1.5,
                    border: '1px solid',
                    borderColor: 'divider',
                    height: 32,
                    '& .MuiToggleButton-root': {
                      border: 'none',
                      px: 1,
                      py: 0.25,
                      color: 'text.secondary',
                      '&.Mui-selected': { bgcolor: 'primary.light', color: 'primary.dark' },
                    },
                  }}
                >
                  <ToggleButton value="grid" aria-label="grid view">
                    <IoGridOutline size={16} />
                  </ToggleButton>
                  <ToggleButton value="table" aria-label="table view">
                    <IoListOutline size={16} />
                  </ToggleButton>
                </ToggleButtonGroup>
              </Stack>
            </Card>

            {/* Error Message */}
            {cohortsError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                Failed to load cohorts directory. Please check network connection.
              </Alert>
            )}

            {/* Loading State */}
            {isLoadingCohorts ? (
              <Box sx={{ py: 8, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                <CircularProgress size={32} />
                <Typography variant="caption" sx={{ mt: 1.5, color: 'text.secondary', fontWeight: 500 }}>
                  Loading cohorts directory...
                </Typography>
              </Box>
            ) : cohorts.length === 0 ? (
              <Card sx={{ p: 5, textAlign: 'center', borderRadius: 2, border: '1px solid', borderColor: 'divider', boxShadow: 'none' }}>
                <SearchNotFound searchQuery={searchText} />
              </Card>
            ) : viewMode === 'grid' ? (
              /* Grid View */
              <Grid container spacing={2}>
                {cohorts.map((cohort) => (
                  <Grid item xs={12} sm={6} lg={4} key={cohort.id}>
                    <CohortCard
                      cohort={cohort}
                      baseRoute={baseRoute}
                      canEdit={canEditCohort}
                      canDelete={canDeleteCohort}
                      canManageTeachers={canManageTeachers}
                      onEdit={() => setEditingCohort(cohort)}
                      onAssignTeachers={() => setAssignTeachersCohort(cohort)}
                      onDelete={() => setCohortToDelete(cohort)}
                    />
                  </Grid>
                ))}
              </Grid>
            ) : (
              /* Table View */
              <Card sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider', boxShadow: 'none', overflow: 'hidden' }}>
                <TableContainer>
                  <Table>
                    <TableHead sx={{ bgcolor: 'grey.50' }}>
                      <TableRow>
                        <TableCell sx={{ py: 1.75, fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', textTransform: 'uppercase' }}>
                          Cohort Name
                        </TableCell>
                        <TableCell sx={{ py: 1.75, fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', textTransform: 'uppercase' }}>
                          Grade Level
                        </TableCell>
                        <TableCell sx={{ py: 1.75, fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', textTransform: 'uppercase' }}>
                          Assigned Teachers
                        </TableCell>
                        <TableCell sx={{ py: 1.75, fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', textTransform: 'uppercase' }}>
                          Enrolled Scholars
                        </TableCell>
                        <TableCell sx={{ py: 1.75, fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', textTransform: 'uppercase' }}>
                          Classroom Vault
                        </TableCell>
                        <TableCell align="right" sx={{ py: 1.75, fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', textTransform: 'uppercase' }}>
                          Actions
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {cohorts.map((cohort) => (
                        <TableRow
                          key={cohort.id}
                          hover
                          sx={{ cursor: 'pointer', transition: 'background-color 0.15s ease', '&:last-child td, &:last-child th': { border: 0 } }}
                          onClick={() => navigate(`${baseRoute}/${cohort.id}`)}
                        >
                          <TableCell sx={{ py: 2 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary', fontSize: '0.875rem' }}>
                              {cohort.name}
                            </Typography>
                            {cohort.description && (
                              <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem', display: 'block' }}>
                                {cohort.description}
                              </Typography>
                            )}
                          </TableCell>

                          <TableCell sx={{ py: 2 }}>
                            <Chip
                              label={cohort.className || 'General Grade'}
                              size="small"
                              sx={{ height: 24, fontSize: '0.75rem', fontWeight: 600, bgcolor: 'primary.light', color: 'primary.dark' }}
                            />
                          </TableCell>

                          <TableCell sx={{ py: 2 }}>
                            {cohort.teachers && cohort.teachers.length > 0 ? (
                              <AvatarGroup max={3} sx={{ justifyContent: 'flex-start' }}>
                                {cohort.teachers.map((t) => (
                                  <Tooltip key={t.id} title={`${t.firstname} ${t.lastname}`}>
                                    <Avatar sx={{ width: 28, height: 28, fontSize: '0.75rem', bgcolor: '#2563EB', color: '#FFFFFF' }}>
                                      {t.firstname?.charAt(0) || 'T'}
                                    </Avatar>
                                  </Tooltip>
                                ))}
                              </AvatarGroup>
                            ) : (
                              <Typography variant="caption" sx={{ color: 'text.disabled', fontStyle: 'italic' }}>
                                No teacher assigned
                              </Typography>
                            )}
                          </TableCell>

                          <TableCell sx={{ py: 2 }}>
                            <Chip
                              label={`${cohort.studentCount || 0} Scholars`}
                              size="small"
                              sx={{ height: 24, fontSize: '0.75rem', fontWeight: 600, bgcolor: 'rgba(16, 185, 129, 0.1)', color: '#059669' }}
                            />
                          </TableCell>

                          <TableCell sx={{ py: 2 }}>
                            <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary', fontSize: '0.875rem' }}>
                              ${(cohort.totalBalance || 0).toLocaleString()}
                            </Typography>
                          </TableCell>

                          <TableCell align="right" sx={{ py: 2 }}>
                            <Stack direction="row" spacing={0.5} justifyContent="flex-end" onClick={(e) => e.stopPropagation()}>
                              <Tooltip title="View Cohort Hub">
                                <IconButton
                                  size="small"
                                  onClick={() => navigate(`${baseRoute}/${cohort.id}`)}
                                  sx={{ p: 0.75, color: 'text.secondary', '&:hover': { bgcolor: 'primary.light', color: 'primary.dark' } }}
                                >
                                  <IoArrowForward size={16} />
                                </IconButton>
                              </Tooltip>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Card>
            )}
          </Box>
        )}

        {/* TAB 1: Academic Classes & Grade Tiers */}
        {currentTab === 1 && canManageClasses && (
          <Card sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider', boxShadow: 'none', overflow: 'hidden' }}>
            {/* Class Controls Bar */}
            <Box
              sx={{
                p: 2,
                borderBottom: '1px solid',
                borderColor: 'divider',
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                alignItems: { xs: 'stretch', md: 'center' },
                justifyContent: 'space-between',
                gap: 1.5,
                bgcolor: 'background.neutral',
              }}
            >
              {/* Search */}
              <TextField
                size="small"
                placeholder="Search academic classes or grade levels..."
                value={classSearchText}
                onChange={(e) => setClassSearchText(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Icon icon="solar:magnifer-linear" width={18} style={{ color: '#94A3B8' }} />
                    </InputAdornment>
                  ),
                  sx: {
                    height: 36,
                    bgcolor: 'background.paper',
                    borderRadius: 1.5,
                    fontSize: '0.8125rem',
                    maxWidth: { xs: '100%', md: 320 },
                  },
                }}
              />

              <Button
                variant="contained"
                size="small"
                startIcon={<Icon icon="solar:add-circle-bold" width={18} />}
                onClick={() => setIsCreateClassOpen(true)}
                sx={{
                  height: 36,
                  px: 2,
                  fontWeight: 600,
                  fontSize: '0.8125rem',
                  borderRadius: 1.5,
                  textTransform: 'none',
                  boxShadow: 'none',
                  '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.15)' },
                }}
              >
                Add Grade Level
              </Button>
            </Box>

            {/* Table */}
            <TableContainer>
              <Table>
                <TableHead sx={{ bgcolor: 'grey.50' }}>
                  <TableRow>
                    <TableCell sx={{ py: 1.75, fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', textTransform: 'uppercase' }}>
                      Grade Level / Class
                    </TableCell>
                    <TableCell sx={{ py: 1.75, fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', textTransform: 'uppercase' }}>
                      Associated Cohort Sections
                    </TableCell>
                    <TableCell sx={{ py: 1.75, fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', textTransform: 'uppercase' }}>
                      Enrolled Scholars
                    </TableCell>
                    <TableCell sx={{ py: 1.75, fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', textTransform: 'uppercase' }}>
                      Created Date
                    </TableCell>
                    <TableCell align="right" sx={{ py: 1.75, fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', textTransform: 'uppercase' }}>
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {isLoadingClasses ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                        <CircularProgress size={32} />
                        <Typography variant="caption" sx={{ mt: 1.25, color: 'text.secondary', fontWeight: 500, display: 'block', fontSize: '0.8125rem' }}>
                          Loading grade levels directory...
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : filteredClasses.length > 0 ? (
                    filteredClasses.map((cls) => {
                      const className = cls.name || cls.title || 'Untitled Class';
                      const enrolledCount = (cls.enrolledStudents || []).length;
                      const associatedCohorts = cohortsByClassId.get(cls.id) || [];

                      return (
                        <TableRow
                          key={cls.id}
                          hover
                          sx={{
                            transition: 'background-color 0.15s ease',
                            '&:last-child td, &:last-child th': { border: 0 },
                          }}
                        >
                          {/* Class Name with Duotone Icon */}
                          <TableCell sx={{ py: 2 }}>
                            <Stack direction="row" alignItems="center" spacing={1.5}>
                              <Box
                                sx={{
                                  width: 36,
                                  height: 36,
                                  borderRadius: 1.5,
                                  bgcolor: 'rgba(37, 99, 235, 0.12)',
                                  color: '#2563EB',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  flexShrink: 0,
                                }}
                              >
                                <Icon icon="solar:diploma-verified-bold-duotone" width={20} />
                              </Box>
                              <Box>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary', fontSize: '0.875rem' }}>
                                  {className}
                                </Typography>
                                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem', fontWeight: 400 }}>
                                  ID: <span style={{ fontFamily: 'monospace' }}>{cls.id.slice(0, 8)}...</span>
                                </Typography>
                              </Box>
                            </Stack>
                          </TableCell>

                          {/* Associated Cohorts */}
                          <TableCell sx={{ py: 2 }}>
                            {associatedCohorts.length > 0 ? (
                              <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                                {associatedCohorts.map((cohort) => (
                                  <Chip
                                    key={cohort.id}
                                    label={cohort.name}
                                    size="small"
                                    onClick={() => navigate(`${baseRoute}/${cohort.id}`)}
                                    sx={{
                                      height: 24,
                                      fontSize: '0.75rem',
                                      fontWeight: 600,
                                      cursor: 'pointer',
                                      bgcolor: 'primary.light',
                                      color: 'primary.dark',
                                      '&:hover': { bgcolor: 'primary.main', color: '#FFFFFF' },
                                    }}
                                  />
                                ))}
                              </Stack>
                            ) : (
                              <Typography variant="caption" sx={{ color: 'text.disabled', fontStyle: 'italic' }}>
                                No cohorts attached
                              </Typography>
                            )}
                          </TableCell>

                          {/* Enrolled Scholars */}
                          <TableCell sx={{ py: 2 }}>
                            <Chip
                              label={`${enrolledCount} Scholars`}
                              size="small"
                              sx={{
                                height: 25,
                                fontSize: '0.8125rem',
                                fontWeight: 700,
                                borderRadius: 1.25,
                                bgcolor: 'rgba(16, 185, 129, 0.1)',
                                color: '#059669',
                              }}
                            />
                          </TableCell>

                          {/* Created Date */}
                          <TableCell sx={{ py: 2 }}>
                            <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 500, fontSize: '0.8125rem' }}>
                              {cls.createdat ? moment(cls.createdat).format('MMM D, YYYY') : '--'}
                            </Typography>
                          </TableCell>

                          {/* Actions: Rename & Delete */}
                          <TableCell align="right" sx={{ py: 2 }}>
                            <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                              <Tooltip title="Rename Grade Level / Class">
                                <IconButton
                                  size="small"
                                  onClick={() => setEditingClass(cls)}
                                  sx={{
                                    p: 0.75,
                                    color: 'text.secondary',
                                    '&:hover': { bgcolor: 'primary.light', color: 'primary.dark' },
                                  }}
                                >
                                  <Icon icon="solar:pen-bold-duotone" width={17} />
                                </IconButton>
                              </Tooltip>

                              <Tooltip title="Delete Grade Level">
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    setClassToDelete({
                                      id: cls.id,
                                      name: className,
                                      enrolledCount,
                                      cohortCount: associatedCohorts.length,
                                      cohorts: associatedCohorts,
                                    })
                                  }
                                  sx={{
                                    p: 0.75,
                                    color: 'text.secondary',
                                    '&:hover': { bgcolor: 'error.lighter', color: 'error.main' },
                                  }}
                                >
                                  <Icon icon="solar:trash-bin-trash-bold-duotone" width={17} />
                                </IconButton>
                              </Tooltip>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  ) : (
                    <TableRow>
                      <TableCell colSpan={5} sx={{ py: 4 }}>
                        <SearchNotFound searchQuery={classSearchText} />
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        )}

        {/* Modal: Create Cohort */}
        <CreateCohortModal
          open={isCreateOpen}
          onClose={() => setIsCreateOpen(false)}
          classes={classes}
          onSubmit={async (dto) => {
            await createCohortMutation.mutateAsync(dto);
          }}
          isSubmitting={createCohortMutation.isPending}
        />

        {/* Modal: Add Grade Level / Class */}
        <CreateClassModal
          open={isCreateClassOpen}
          onClose={() => setIsCreateClassOpen(false)}
          onSubmit={async (title) => {
            await createClassMutation.mutateAsync(title);
          }}
          isSubmitting={createClassMutation.isPending}
        />

        {/* Modal: Edit / Rename Grade Level / Class */}
        <EditClassModal
          open={Boolean(editingClass)}
          onClose={() => setEditingClass(null)}
          classItem={editingClass}
          onSubmit={async (id, title) => {
            await updateClassMutation.mutateAsync({ id, title });
          }}
          isSubmitting={updateClassMutation.isPending}
        />

        {/* Modal: Delete Grade Level / Class */}
        <DeleteClassModal
          open={Boolean(classToDelete)}
          onClose={() => setClassToDelete(null)}
          classItem={classToDelete}
          onViewCohorts={(classId) => {
            handleClassFilterClick(classId);
            setCurrentTab(0);
          }}
          onConfirm={async (id) => {
            await deleteClassMutation.mutateAsync(id);
          }}
          isDeleting={deleteClassMutation.isPending}
        />

        {/* Modal: Edit Cohort */}
        <EditCohortModal
          open={Boolean(editingCohort)}
          onClose={() => setEditingCohort(null)}
          cohort={editingCohort}
          classes={classes}
          onSubmit={async (id, dto) => {
            await updateCohortMutation.mutateAsync({ id, dto });
          }}
          isSubmitting={updateCohortMutation.isPending}
        />

        {/* Modal: Assign Teachers to Cohort */}
        <AssignTeachersModal
          open={Boolean(assignTeachersCohort)}
          onClose={() => setAssignTeachersCohort(null)}
          cohort={assignTeachersCohort}
          onSubmit={async (cohortId, teacherIds) => {
            await assignTeachersMutation.mutateAsync({ cohortId, teacherIds });
          }}
          isSubmitting={assignTeachersMutation.isPending}
        />

        {/* Modal: Delete Cohort Confirmation */}
        <DeleteCohortModal
          open={Boolean(cohortToDelete)}
          onClose={() => setCohortToDelete(null)}
          cohort={cohortToDelete}
          onConfirm={async (id) => {
            await deleteCohortMutation.mutateAsync(id);
          }}
          isDeleting={deleteCohortMutation.isPending}
        />
      </Box>
    </Page>
  );
}
